import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import "./Floorplan.css";

// SVG is loaded dynamically to avoid bundling 1.5MB into the JS chunk
const FLOORPLAN_SVG_URL = "/assets/floorplan.svg";

export interface FloorplanProps {
  hoveredRoom?: string | null;
  selectedRoom?: string | null;
  onRoomHover?: (roomId: string | null) => void;
  onRoomSelect?: (roomId: string) => void;
  onBackgroundClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const INTERACTIVE_SELECTOR = "[data-room-id]";

export const Floorplan: React.FC<FloorplanProps> = ({
  hoveredRoom,
  selectedRoom,
  onRoomHover,
  onRoomSelect,
  onBackgroundClick,
  className,
  style,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load SVG content dynamically
  useEffect(() => {
    let cancelled = false;

    fetch(FLOORPLAN_SVG_URL)
      .then((res) => res.text())
      .then((content) => {
        if (!cancelled) {
          setSvgContent(content);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load floorplan SVG:", err);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const getRoomFromEvent = (event: Event): string | null => {
      const target = (event.target as Element | null)?.closest<SVGElement>(
        INTERACTIVE_SELECTOR,
      );
      return target?.getAttribute("data-room-id") ?? null;
    };

    const handleMouseOver = (event: MouseEvent) => {
      const roomId = getRoomFromEvent(event);
      if (roomId) {
        onRoomHover?.(roomId);
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      const related = (event.relatedTarget as Element | null)?.closest(
        INTERACTIVE_SELECTOR,
      );
      if (!related) {
        onRoomHover?.(null);
      }
    };

    const handleClick = (event: MouseEvent) => {
      const roomId = getRoomFromEvent(event);
      if (roomId) {
        event.stopPropagation();
        onRoomSelect?.(roomId);
      } else {
        onBackgroundClick?.();
      }
    };

    container.addEventListener("mouseover", handleMouseOver);
    container.addEventListener("mouseout", handleMouseOut);
    container.addEventListener("click", handleClick);

    return () => {
      container.removeEventListener("mouseover", handleMouseOver);
      container.removeEventListener("mouseout", handleMouseOut);
      container.removeEventListener("click", handleClick);
    };
  }, [svgContent, onRoomHover, onRoomSelect, onBackgroundClick]);

  // Apply default fills for SVG elements (CSS fallback for production)
  // The Floorplan.css import doesn't reliably load in production builds
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !svgContent) return;

    // Apply semi-transparent fill to bookable rooms
    container.querySelectorAll(".bookable").forEach((el) => {
      const svgEl = el as SVGElement;
      svgEl.style.fill = "rgba(65, 65, 56, 0.2)";
      svgEl.style.cursor = "pointer";
      svgEl.style.transition = "fill 0.2s ease";
    });

    // Apply transparent fill to non-interactive areas
    container
      .querySelectorAll(".unbookable, .inventar, .door")
      .forEach((el) => {
        (el as SVGElement).style.fill = "none";
      });
  }, [svgContent]);

  // Update highlight styles when hovered/selected room changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !svgContent) return;

    const updateHighlight = (className: string, roomId?: string | null) => {
      // Remove highlight from all rooms
      container.querySelectorAll(`.bookable.${className}`).forEach((node) => {
        node.classList.remove(className);
        // Reset to default fill when removing highlight
        (node as SVGElement).style.fill = "rgba(65, 65, 56, 0.2)";
      });

      if (!roomId) {
        return;
      }

      // Add highlight to target room
      const target = container.querySelector<SVGElement>(
        `${INTERACTIVE_SELECTOR}[data-room-id="${roomId}"] .bookable`,
      );
      if (target) {
        target.classList.add(className);
        // Apply highlight fill color
        if (className === "activeRoom") {
          target.style.fill = "rgba(11, 204, 218, 0.7)"; // Cyan for hover
        } else if (className === "selectedRoom") {
          target.style.fill = "rgba(255, 222, 89, 0.75)"; // Yellow for selected
        }
      }
    };

    updateHighlight("activeRoom", hoveredRoom);
    updateHighlight("selectedRoom", selectedRoom);
  }, [svgContent, hoveredRoom, selectedRoom]);

  if (isLoading || !svgContent) {
    return (
      <div
        className={className}
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
        }}
        data-testid="floorplan-loading"
      >
        <span>Laster etasjeplan...</span>
      </div>
    );
  }

  // SECURITY: SVG content is sanitized with DOMPurify to prevent XSS attacks.
  // We still use dangerouslySetInnerHTML because we need direct DOM access
  // to the SVG elements for interactive features (event listeners, styling).
  // The SVG source is a static asset (/assets/floorplan.svg), but sanitization
  // provides defense-in-depth protection.
  const sanitizedSvg = DOMPurify.sanitize(svgContent, {
    USE_PROFILES: { svg: true, svgFilters: true },
  });

  return (
    <div
      ref={containerRef}
      className={className}
      style={style}
      dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
      data-testid="floorplan-svg"
    />
  );
};

Floorplan.displayName = "Floorplan";

export default Floorplan;
